import React, { useState } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Typography,
  CircularProgress,
  Tooltip,
  Modal,
  Backdrop,
  Fade,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Download as DownloadIcon,
  PictureAsPdf as PdfIcon,
  Description as WordIcon,
  TableChart as ExcelIcon,
  Image as ImageIcon,
  VideoFile as VideoIcon,
  AudioFile as AudioIcon,
  Archive as ArchiveIcon,
  Code as CodeIcon,
  TextSnippet as TextIcon,
  InsertDriveFile as FileIcon,
  RestoreFromTrash as RestoreIcon,
} from '@mui/icons-material';
import { FileMetadataDTO } from '../../api/generated/requests/types.gen';
import { MinIoFileStorageService } from '../../api/generated/requests/services.gen';
import { useSnackbar } from 'notistack';
import LazyThumbnail from './LazyThumbnail';

// Component to display file type icons based on content type
const FileTypeIcon: React.FC<{ contentType?: string; size?: 'small' | 'medium' | 'large' }> = ({ 
  contentType, 
  size = 'small' 
}) => {
  const getFileIcon = (mimeType?: string) => {
    if (!mimeType) return <FileIcon fontSize={size} />;

    const type = mimeType.toLowerCase();

    // PDF files
    if (type.includes('pdf')) {
      return <PdfIcon fontSize={size} sx={{ color: '#d32f2f' }} />;
    }

    // Word documents
    if (type.includes('word') || type.includes('msword') || 
        type.includes('officedocument.wordprocessingml')) {
      return <WordIcon fontSize={size} sx={{ color: '#1976d2' }} />;
    }

    // Excel files
    if (type.includes('excel') || type.includes('spreadsheet') || 
        type.includes('officedocument.spreadsheetml')) {
      return <ExcelIcon fontSize={size} sx={{ color: '#388e3c' }} />;
    }

    // PowerPoint files
    if (type.includes('powerpoint') || type.includes('presentation') ||
        type.includes('officedocument.presentationml')) {
      return <WordIcon fontSize={size} sx={{ color: '#f57c00' }} />;
    }

    // Images
    if (type.startsWith('image/')) {
      return <ImageIcon fontSize={size} sx={{ color: '#7b1fa2' }} />;
    }

    // Videos
    if (type.startsWith('video/')) {
      return <VideoIcon fontSize={size} sx={{ color: '#c2185b' }} />;
    }

    // Audio files
    if (type.startsWith('audio/')) {
      return <AudioIcon fontSize={size} sx={{ color: '#00796b' }} />;
    }

    // Archive files
    if (type.includes('zip') || type.includes('rar') || type.includes('7z') || 
        type.includes('tar') || type.includes('gzip')) {
      return <ArchiveIcon fontSize={size} sx={{ color: '#5d4037' }} />;
    }

    // Code files
    if (type.includes('javascript') || type.includes('json') || type.includes('xml') ||
        type.includes('html') || type.includes('css') || type.includes('text/plain')) {
      return <CodeIcon fontSize={size} sx={{ color: '#455a64' }} />;
    }

    // Text files
    if (type.startsWith('text/')) {
      return <TextIcon fontSize={size} sx={{ color: '#616161' }} />;
    }

    // Default file icon
    return <FileIcon fontSize={size} sx={{ color: '#757575' }} />;
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      {getFileIcon(contentType)}
    </Box>
  );
};

// Utility functions
const formatFileSize = (bytes?: number): string => {
  if (bytes === undefined) return 'Nieznany';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

const formatDate = (dateString?: string): string => {
  if (!dateString) return 'Nieznana';
  try {
    const date = new Date(dateString);
    return date.toLocaleString('pl-PL');
  } catch {
    return dateString;
  }
};

interface FileListProps {
  files: FileMetadataDTO[];
  isLoading: boolean;
  onDownload: (filename: string, originalFilename?: string) => void;
  onDelete: (file: FileMetadataDTO) => void;
  onRestore?: (file: FileMetadataDTO) => void;
  onPreview?: (file: FileMetadataDTO) => void;
  compact?: boolean; // For dialog usage
  enablePreview?: boolean; // Enable built-in preview functionality
}

const FileList: React.FC<FileListProps> = ({
  files,
  isLoading,
  onDownload,
  onDelete,
  onRestore,
  onPreview,
  compact = false,
  enablePreview = false
}) => {
  const { enqueueSnackbar } = useSnackbar();

  // Preview state variables
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);
  const [previewFileName, setPreviewFileName] = useState<string>('');
  const [confirmPreviewOpen, setConfirmPreviewOpen] = useState(false);
  const [fileToPreview, setFileToPreview] = useState<FileMetadataDTO | null>(null);

  // Preview functionality
  const handleFilePreview = async (file: FileMetadataDTO) => {
    if (!file.filename || !file.contentType) {
      enqueueSnackbar('Brak informacji o pliku', { variant: 'error' });
      return;
    }

    const contentType = file.contentType.toLowerCase();

    // Handle images
    if (contentType.startsWith('image/')) {
      try {
        // Get the download URL from the API
        const response = await MinIoFileStorageService.getApiFilesByFilenameDownload({
          filename: file.filename
        });

        // The response contains downloadUrl
        const downloadUrl = (response as any).downloadUrl;
        if (!downloadUrl) {
          throw new Error('No download URL in response');
        }

        // Fetch the actual image using the download URL
        const imageResponse = await fetch(downloadUrl);
        if (!imageResponse.ok) {
          throw new Error(`HTTP error! status: ${imageResponse.status}`);
        }

        const blob = await imageResponse.blob();
        const imageUrl = URL.createObjectURL(blob);
        setPreviewImageUrl(imageUrl);
        setPreviewFileName(file.originalFilename || file.filename);
        setPreviewModalOpen(true);
      } catch (error) {
        console.error('Error loading image preview:', error);
        enqueueSnackbar('Błąd podczas ładowania podglądu obrazu', { variant: 'error' });
      }
      return;
    }

    // Handle PDF files
    if (contentType.includes('pdf')) {
      try {
        // Get the download URL from the API
        const response = await MinIoFileStorageService.getApiFilesByFilenameDownload({
          filename: file.filename
        });

        // The response contains downloadUrl
        const downloadUrl = (response as any).downloadUrl;
        if (!downloadUrl) {
          throw new Error('No download URL in response');
        }

        // Open the PDF using the download URL directly
        window.open(downloadUrl, '_blank');
      } catch (error) {
        console.error('Error opening PDF:', error);
        enqueueSnackbar('Błąd podczas otwierania pliku PDF', { variant: 'error' });
      }
      return;
    }

    // Handle other file types - show confirmation dialog
    setFileToPreview(file);
    setConfirmPreviewOpen(true);
  };

  const handlePreviewConfirm = () => {
    if (fileToPreview) {
      onDownload(fileToPreview.filename!, fileToPreview.originalFilename);
    }
    setConfirmPreviewOpen(false);
    setFileToPreview(null);
  };

  const handleClosePreviewModal = () => {
    setPreviewModalOpen(false);
    if (previewImageUrl) {
      URL.revokeObjectURL(previewImageUrl);
      setPreviewImageUrl(null);
    }
    setPreviewFileName('');
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: compact ? 0.5 : 3 }}>
        <CircularProgress size={compact ? 16 : 24} />
      </Box>
    );
  }

  if (files.length === 0) {
    return (
      <Typography 
        align="center" 
        color="text.secondary" 
        variant={compact ? "caption" : "body2"}
        sx={{ py: compact ? 0.5 : 2, display: 'block' }}
      >
        Brak załączników
      </Typography>
    );
  }

  const handleRowClick = (file: FileMetadataDTO) => {
    if (enablePreview) {
      handleFilePreview(file);
    } else if (onPreview) {
      onPreview(file);
    }
  };

  return (
    <>
      <TableContainer sx={{ 
        maxHeight: compact ? '300px' : 'auto',
        overflowX: 'auto'
      }}>
        <Table size={compact ? 'small' : 'medium'}>
          <TableHead>
            <TableRow sx={{ height: compact ? 32 : 40 }}>
              <TableCell
                align="center"
                sx={{
                  minWidth: compact ? 40 : 60,
                  backgroundColor: '#ffffff',
                  color: '#1f2937',
                  fontWeight: 600,
                  fontSize: compact ? '0.7rem' : '0.8rem',
                  borderBottom: '1px solid #e5e7eb',
                  px: compact ? 0.5 : 1,
                  py: compact ? 0.25 : 0.5,
                }}
              >
                {compact ? '' : 'Miniatura'}
              </TableCell>
              <TableCell
                sx={{
                  minWidth: compact ? 100 : 150,
                  backgroundColor: '#ffffff',
                  color: '#1f2937',
                  fontWeight: 600,
                  fontSize: compact ? '0.7rem' : '0.8rem',
                  borderBottom: '1px solid #e5e7eb',
                  px: compact ? 0.5 : 1,
                  py: compact ? 0.25 : 0.5,
                }}
              >
                Nazwa pliku
              </TableCell>
              <TableCell
                align="center"
                sx={{
                  minWidth: compact ? 40 : 80,
                  backgroundColor: '#ffffff',
                  color: '#1f2937',
                  fontWeight: 600,
                  fontSize: compact ? '0.7rem' : '0.8rem',
                  borderBottom: '1px solid #e5e7eb',
                  px: compact ? 0.5 : 1,
                  py: compact ? 0.25 : 0.5,
                }}
              >
                Typ
              </TableCell>
              <TableCell
                sx={{
                  minWidth: compact ? 60 : 80,
                  backgroundColor: '#ffffff',
                  color: '#1f2937',
                  fontWeight: 600,
                  fontSize: compact ? '0.7rem' : '0.8rem',
                  borderBottom: '1px solid #e5e7eb',
                  px: compact ? 0.5 : 1,
                  py: compact ? 0.25 : 0.5,
                }}
              >
                Rozmiar
              </TableCell>
              {!compact && (
                <TableCell
                  sx={{
                    minWidth: 120,
                    backgroundColor: '#ffffff',
                    color: '#1f2937',
                    fontWeight: 600,
                    fontSize: '0.8rem',
                    borderBottom: '1px solid #e5e7eb',
                    px: 1,
                    py: 0.5,
                  }}
                >
                  Data dodania
                </TableCell>
              )}
              <TableCell
                align="right"
                sx={{
                  minWidth: compact ? 80 : 120,
                  backgroundColor: '#ffffff',
                  color: '#1f2937',
                  fontWeight: 600,
                  fontSize: compact ? '0.7rem' : '0.8rem',
                  borderBottom: '1px solid #e5e7eb',
                  px: compact ? 0.5 : 1,
                  py: compact ? 0.25 : 0.5,
                }}
              >
                Akcje
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {files.map((file, index) => (
              <TableRow
                key={file.filename || index}
                hover={!!(onPreview || enablePreview)}
                onClick={() => handleRowClick(file)}
                sx={{
                  backgroundColor: index % 2 === 0 ? '#ffffff' : '#f9fafb',
                  transition: 'all 0.2s ease-in-out',
                  cursor: (onPreview || enablePreview) ? 'pointer' : 'default',
                  '&:hover': (onPreview || enablePreview) ? {
                    backgroundColor: '#e0f2fe',
                  } : {},
                  height: compact ? '28px' : '36px',
                  '& td': {
                    py: compact ? '2px' : '4px',
                    px: compact ? 0.5 : 1,
                    fontSize: compact ? '0.7rem' : '0.8rem',
                  },
                }}
              >
                <TableCell align="center">
                  {file.filename && (
                    <LazyThumbnail 
                      filename={file.filename} 
                      size={150}
                      quality={100}
                    />
                  )}
                </TableCell>
                <TableCell 
                  sx={{ 
                    maxWidth: compact ? '100px' : '150px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {file.originalFilename || file.filename}
                </TableCell>
                <TableCell align="center">
                  <FileTypeIcon contentType={file.contentType} size="small" />
                </TableCell>
                <TableCell>{formatFileSize(file.fileSize)}</TableCell>
                {!compact && (
                  <TableCell>{formatDate(file.createdAt)}</TableCell>
                )}
                <TableCell align="right">
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'flex-end',
                      gap: compact ? 0.25 : 0.5,
                      minWidth: 'fit-content'
                    }}
                    onClick={(e) => e.stopPropagation()} // Prevent row click when clicking actions
                  >
                    <Tooltip title="Pobierz">
                      <IconButton 
                        color="primary" 
                        size={compact ? "small" : "medium"}
                        onClick={() => file.filename && onDownload(file.filename, file.originalFilename)}
                        sx={{ 
                          p: compact ? 0.25 : 0.5,
                          '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.04)' }
                        }}
                      >
                        <DownloadIcon sx={{ fontSize: compact ? '0.9rem' : '1.1rem' }} />
                      </IconButton>
                    </Tooltip>
                    {onRestore && file.status === 'DETACHED' && (
                      <Tooltip title="Przywróć z kosza">
                        <IconButton 
                          color="success" 
                          size={compact ? "small" : "medium"}
                          onClick={() => onRestore(file)}
                          sx={{ 
                            p: compact ? 0.25 : 0.5,
                            '&:hover': { backgroundColor: 'rgba(46, 125, 50, 0.04)' }
                          }}
                        >
                          <RestoreIcon sx={{ fontSize: compact ? '0.9rem' : '1.1rem' }} />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Usuń">
                      <IconButton 
                        color="error" 
                        size={compact ? "small" : "medium"}
                        onClick={() => onDelete(file)}
                        sx={{ 
                          p: compact ? 0.25 : 0.5,
                          '&:hover': { backgroundColor: 'rgba(211, 47, 47, 0.04)' }
                        }}
                      >
                        <DeleteIcon sx={{ fontSize: compact ? '0.9rem' : '1.1rem' }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Preview Modals - only render when enablePreview is true */}
      {enablePreview && (
        <>
          {/* Image Preview Modal */}
          <Modal
            open={previewModalOpen}
            onClose={handleClosePreviewModal}
            closeAfterTransition
            BackdropComponent={Backdrop}
            BackdropProps={{
              timeout: 500,
            }}
          >
            <Fade in={previewModalOpen}>
              <Box
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  maxWidth: '90vw',
                  maxHeight: '90vh',
                  bgcolor: 'background.paper',
                  border: '2px solid #000',
                  boxShadow: 24,
                  p: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
                  {previewFileName}
                </Typography>
                {previewImageUrl && (
                  <Box
                    component="img"
                    src={previewImageUrl}
                    alt={previewFileName}
                    sx={{
                      maxWidth: '100%',
                      maxHeight: 'calc(90vh - 120px)',
                      objectFit: 'contain',
                    }}
                  />
                )}
                <Button
                  onClick={handleClosePreviewModal}
                  sx={{ mt: 2 }}
                  variant="contained"
                >
                  Zamknij
                </Button>
              </Box>
            </Fade>
          </Modal>

          {/* Confirmation Dialog for Unsupported Files */}
          <Dialog
            open={confirmPreviewOpen}
            onClose={() => setConfirmPreviewOpen(false)}
          >
            <DialogTitle>Podgląd niedostępny</DialogTitle>
            <DialogContent>
              <DialogContentText>
                Szybki podgląd nie możliwy, chcesz pobrać plik?
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setConfirmPreviewOpen(false)}>
                Anuluj
              </Button>
              <Button onClick={handlePreviewConfirm} variant="contained">
                Pobierz
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </>
  );
};

export default FileList;
